import React from "react";
import { connect } from "react-redux";

import Dialog from "../dialog/Dialog";
import DialogContent from "../dialog/DialogContent";
import { IAppState } from "../../state/Store";
import Panel from "../panels/Panel";
import { Alignment, Side, Size } from "../../types/system/Layout";
import Textbox from "../inputform/Textbox";
import Button from "../buttons/Button";
import ButtonThemes from "../../types/enums/ButtonThemes";
import GeneralContainer from "../containers/GeneralContainer";
import Info from "../info/Info";
import Error from "../error/Error";
import Datastore from "../../communication/Datastore";

import "./ReauthDialog.scss";
import {
    AuthenticationResponse,
    ResponseType,
} from "../../types/system/AuthenticationResponse";
import User from "../../types/core/User";
import Utilities from "../../foundation/Utility";
import ApplicationState from "../../foundation/AppState";
import Fido2PublicKeyCredentialCreationOptions from "../../types/fido2/Fido2PublicKeyCredentialCreationOptions";
import Fido2PublicKeyCredentialRequestOptions from "../../types/fido2/Fido2PublicKeyCredentialRequestOptions";

enum AuthenticationModes {
    Unset = "Unset",
    TDS = "TDS",
    SAML = "SAML",
    OpenID = "OpenID",
    Passwordless = "Passwordless",
    TwoFA = "TwoFA",
}

interface ReauthDialogProps {
    user: User;
    open: boolean;
    title: string;

    onSuccess?: () => void;
    onCancel?: () => void;
}

interface ReauthDialogState {
    loading: boolean;
    mode: AuthenticationModes;
    display: AuthenticationModes;
    response: AuthenticationResponse;
    status: string;

    password?: string;
    loginWindow?: Window;
}

class ReauthDialog extends React.Component<
    ReauthDialogProps,
    ReauthDialogState
> {
    state: ReauthDialogState = {
        loading: false,
        mode: AuthenticationModes.Unset,
        display: AuthenticationModes.Unset,
        response: null,
        status: "",
    };

    componentDidUpdate(prevProps: Readonly<ReauthDialogProps>): void {
        if (this.props.open && !prevProps.open) this.initialize();
    }

    async initialize() {
        this.setState({
            loading: true,
            mode: AuthenticationModes.Unset,
            display: AuthenticationModes.Unset,
            status: "",
        });

        const mode = await Datastore.postType<AuthenticationModes>(
            "api/Authentication/AuthenticationMode",
        );

        this.setState({ loading: false, mode, display: mode });
    }

    switchToPassword() {
        this.setState({ display: AuthenticationModes.TDS });
    }

    async authenticatePassword(password: string) {
        try {
            this.setState({ loading: true });

            const response = await Datastore.postType<AuthenticationResponse>(
                "api/Authentication/Verify",
                { username: this.props.user.username, password },
            );

            this.parseResponse(response, password);
        } catch (error) {
            this.failed(error);
        } finally {
            this.setState({ loading: false });
        }
    }

    async authenticateSSO() {
        try {
            this.setState({ loading: true });

            const token = await this.performAuthAppLogin();

            if (!!token) {
                await ApplicationState.instance.updateToken(token);

                this.props.onSuccess?.();
            }
        } catch (error) {
            this.failed(error);
        } finally {
            this.setState({ loading: false });
        }
    }

    async authenticatePasskey() {
        const publicKeyOptions =
            await Datastore.postType<Fido2PublicKeyCredentialRequestOptions>(
                "api/Authentication/AuthenticateFido2/Options/Authenticated",
            );

        // Validate the challenge response
        if (publicKeyOptions.status !== "ok") {
            this.failed(publicKeyOptions.errorMessage);
            return;
        }

        // Clean up the response object
        delete publicKeyOptions.errorMessage;
        delete publicKeyOptions.status;
        delete publicKeyOptions.timeout;

        // Convert challenge from base64 to ArrayBuffer
        publicKeyOptions.challenge = String(
            publicKeyOptions.challenge,
        ).fromBase64() as BufferSource;

        // Build the allowCredentials array with proper structure
        publicKeyOptions.allowCredentials = [];
        for (const item of publicKeyOptions.allowCredentials) {
            publicKeyOptions.allowCredentials.push({
                id: String(item.id).fromBase64() as BufferSource,
                type: "public-key",
            });
        }

        publicKeyOptions.hints = ["hybrid"];
        publicKeyOptions.userVerification = "required";

        // Ask browser for credentials (browser will ask connected authenticators)
        let credential;
        try {
            this.setState({ loading: true });

            credential = await navigator.credentials.get({
                publicKey: publicKeyOptions,
            });

            try {
                const response =
                    await Datastore.postType<AuthenticationResponse>(
                        "api/Authentication/AuthenticateFido2",
                        credential,
                    );

                this.parseResponse(response);
            } catch (error) {
                this.failed(error);
            }
            return;
        } catch (error) {
            this.failed(error);
            return;
        } finally {
            this.setState({ loading: false });
        }
    }

    async authenticate2FA(code: string) {
        try {
            this.setState({ loading: true });

            const response = await Datastore.postType<AuthenticationResponse>(
                "api/Authentication/Verify",
                {
                    username: this.props.user.username,
                    password: this.state.password,
                    secondFactor: code,
                },
            );

            this.parseResponse(response, this.state.password);
        } catch (error) {
            this.failed(error);
        } finally {
            this.setState({ loading: false });
        }
    }

    async parseResponse(response: AuthenticationResponse, password?: string) {
        this.setState({ response });

        switch (response.response) {
            case ResponseType.Success:
                this.setState({
                    status: "",
                    display: AuthenticationModes.Unset,
                    loading: false,
                });

                await ApplicationState.instance.updateToken(
                    response.authenticationIdentifier,
                );

                this.props.onSuccess?.();
                break;
            case ResponseType.IncorrectUsernamePassword:
                this.setState({
                    status: "The username or password entered was invalid.",
                });
                break;
            case ResponseType.AccountDeactivated:
                this.setState({
                    status:
                        "Your account has been deactivated or removed " +
                        "from the system.",
                });
                break;
            case ResponseType.NoSystemAccess:
                this.setState({
                    status:
                        "You were authenticated successfully but do not " +
                        "have access to this system.",
                });
                break;
            case ResponseType.Unset:
                this.setState({ status: "An unknown error occurred." });
                break;
            case ResponseType.Timeout:
                this.setState({
                    status:
                        "Your account is timed out due to too many " +
                        "failed attempts, please try again later.",
                });
                break;
            case ResponseType.Locked:
                this.setState({
                    status:
                        "Your account has been locked due to too many " +
                        "failed attempts, please follow the recovery " +
                        "process to reactivate it.",
                });
                break;
            case ResponseType.TwoFactorRequired:
                this.setState({
                    status:
                        "You are required to provide a second factor " +
                        "for sign-in.",
                    display: AuthenticationModes.TwoFA,
                    password: password,
                });
                break;
            case ResponseType.TwoFactorIncorrect:
                this.setState({
                    status: "The provided second factor was incorrect.",
                    display: AuthenticationModes.TwoFA,
                    password: password,
                });
                break;
            case ResponseType.NoValidSubscription:
                this.setState({
                    status:
                        "You do not have a valid subscription to this " +
                        "application.",
                });
                break;
            case ResponseType.DatabaseMigration:
                this.setState({
                    status:
                        "The system is migrating your school's data at " +
                        "present, please wait until it has completed.",
                });
                break;
        }

        this.setState({ loading: false });
    }

    failed(error: any) {
        this.setState({
            status: error.message,
            display: this.state.mode,
            loading: false,
        });
    }

    performAuthAppLogin(): Promise<string> {
        return new Promise(resolve => {
            const location = {
                top: window.screenY + (window.outerHeight - window.innerHeight),
                left: window.screenX + window.outerWidth / 2 - 250,
            };
            const loc = document.location;

            window["reauthInfoReceiver"] = async (data: string) => {
                if (!!data) {
                    delete window["reauthInfoReceiver"];
                    this.state.loginWindow?.close();
                    resolve(data);
                }
            };

            const loginWindow = window.open(
                `${ApplicationState.instance.getAuthPath(
                    `${loc.protocol}//${loc.host}/AuthHelper.html`,
                )}&forceReauth=true&ssoReauth=true`,
                "reAuthWindow",
                "width=500,height=700,location=no,menubar=no,status=no,titlebar=no,resizable=no,top=" +
                    location.top +
                    ",left=" +
                    location.left,
            );
            loginWindow?.moveTo(location.left, location.top);

            this.setState({ loginWindow });
        });
    }

    cancel() {
        this.props.onCancel?.();
    }

    renderSwitch() {
        switch (this.state.display) {
            case AuthenticationModes.TDS:
                return (
                    <ReauthPassword
                        status={this.state.status}
                        onComplete={this.authenticatePassword.bind(this)}
                    />
                );
            case AuthenticationModes.SAML:
            case AuthenticationModes.OpenID:
                return (
                    <ReauthSSO onComplete={this.authenticateSSO.bind(this)} />
                );
            case AuthenticationModes.Passwordless:
                return (
                    <ReauthPasskey
                        status={this.state.status}
                        onComplete={this.authenticatePasskey.bind(this)}
                        onPassword={this.switchToPassword.bind(this)}
                    />
                );
            case AuthenticationModes.TwoFA:
                return (
                    <Reauth2FA
                        status={this.state.status}
                        onComplete={this.authenticate2FA.bind(this)}
                    />
                );
            default:
                return null;
        }
    }

    render(): React.ReactNode {
        return (
            <>
                <Dialog
                    dialogTitle="Reauthenticate"
                    dialogIcon="lock"
                    showDialog={this.props.open}
                    showLoading={this.state.loading}
                    canResize={false}
                    width={500}
                    zIndex={9999999}>
                    <DialogContent>
                        <Panel
                            bufferSide={Side.All}
                            style={{ minHeight: 100 }}>
                            {this.renderSwitch()}

                            <GeneralContainer
                                fullWidth={true}
                                clearanceSide={Side.Top}>
                                <Button
                                    text="Cancel"
                                    className="half"
                                    align={Alignment.Center}
                                    onClick={() => this.cancel()}
                                />
                            </GeneralContainer>
                        </Panel>
                    </DialogContent>
                </Dialog>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { session } = state.session;
    return {
        user: session.user,
    };
};

const mapDispatchToProps = dispatch => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(ReauthDialog);

interface ReauthPasswordProps {
    status?: string;

    onComplete?: (password: string) => void;
}

interface ReauthPasswordState {
    password: string;
}

class ReauthPassword extends React.Component<
    ReauthPasswordProps,
    ReauthPasswordState
> {
    state: ReauthPasswordState = {
        password: "",
    };

    completeClick() {
        this.props.onComplete?.(this.state.password);
        this.setState({ password: "" });
    }

    render(): React.ReactNode {
        return (
            <>
                <Info clearanceSide={Side.Bottom}>
                    You are required to confirm your credentials to perform this
                    action. Please provide your password to verify yourself.
                </Info>
                {this.props.status && (
                    <Error
                        clearanceSide={Side.Top}
                        bufferSide={Side.Horizontal}
                        bufferSize={Size.XtraLarge}
                        alignment={Alignment.Center}
                        wrappable={true}>
                        {this.props.status}
                    </Error>
                )}
                <GeneralContainer
                    className="half"
                    alignment={Alignment.Center}
                    clearanceSide={Side.Vertical}
                    clearanceSize={Size.XtraLarge}>
                    <Textbox
                        type="password"
                        autoComplete="current-password"
                        className="password full stand-alone"
                        placeholder="Password"
                        value={this.state.password}
                        onChange={value =>
                            this.setState({
                                password: value,
                            })
                        }
                        onKeyUp={event => {
                            if (event.key === "Enter") this.completeClick();
                        }}
                    />
                </GeneralContainer>
                <Button
                    className="half"
                    icon="password"
                    text="Verify Password"
                    align={Alignment.Center}
                    theme={ButtonThemes.Auth}
                    onClick={this.completeClick.bind(this)}
                />
            </>
        );
    }
}

interface ReauthSSOProps {
    status?: string;

    onComplete?: () => void;
}

class ReauthSSO extends React.Component<ReauthSSOProps> {
    render(): React.ReactNode {
        return (
            <>
                <Info clearanceSide={Side.Bottom}>
                    You are required to confirm your credentials to perform this
                    action. Please provide your single sign-on credentials.
                </Info>
                {this.props.status && (
                    <Error
                        clearanceSide={Side.Top}
                        alignment={Alignment.Center}>
                        {this.props.status}
                    </Error>
                )}
                <Button
                    className="half"
                    icon="badge"
                    text="Verify SSO"
                    align={Alignment.Center}
                    theme={ButtonThemes.Auth}
                    onClick={this.props.onComplete}
                />
            </>
        );
    }
}

interface ReauthPasskeyProps {
    status?: string;

    onComplete?: () => void;
    onPassword?: () => void;
}

class ReauthPasskey extends React.Component<ReauthPasskeyProps> {
    render(): React.ReactNode {
        return (
            <>
                <Info clearanceSide={Side.Bottom}>
                    You are required to confirm your credentials to perform this
                    action. Please provide your passkey to verify yourself.
                </Info>
                {this.props.status && (
                    <Error
                        clearanceSide={Side.Top}
                        alignment={Alignment.Center}>
                        {this.props.status}
                    </Error>
                )}
                <Button
                    className="half"
                    icon="passkey"
                    text="Verify Passkey"
                    align={Alignment.Center}
                    theme={ButtonThemes.Auth}
                    clearanceSide={Side.Top}
                    clearanceSize={Size.XtraLarge}
                    onClick={this.props.onComplete}
                />
                <Button
                    className="half"
                    icon="password"
                    text="Use Password"
                    align={Alignment.Center}
                    theme={ButtonThemes.Auth}
                    clearanceSide={Side.Top}
                    onClick={this.props.onPassword}
                />
            </>
        );
    }
}

interface Reauth2FAProps {
    status?: string;

    onComplete?: (code: string) => void;
}

interface Reauth2FAState {
    code: string;
}

class Reauth2FA extends React.Component<Reauth2FAProps, Reauth2FAState> {
    state: Reauth2FAState = {
        code: "",
    };

    completeClick() {
        this.setState({ code: "" });
        this.props.onComplete?.(this.state.code);
    }

    render(): React.ReactNode {
        return (
            <>
                <Info clearanceSide={Side.Bottom}>
                    You are required to confirm your credentials to perform this
                    action. Please provide your two-factor code.
                </Info>
                {this.props.status && (
                    <Error
                        clearanceSide={Side.Top}
                        alignment={Alignment.Center}>
                        {this.props.status}
                    </Error>
                )}
                <GeneralContainer
                    className="half"
                    alignment={Alignment.Center}
                    clearanceSide={Side.Vertical}
                    clearanceSize={Size.XtraLarge}>
                    <Textbox
                        type="text"
                        autoComplete="one-time-code"
                        className="one-time-code"
                        placeholder="000000"
                        value={this.state.code}
                        onChange={value =>
                            this.setState({
                                code: value,
                            })
                        }
                        onKeyUp={event => {
                            if (event.key === "Enter") this.completeClick();
                        }}
                    />
                </GeneralContainer>
                <Button
                    className="half"
                    icon="key"
                    text="Verify Two-Factor"
                    align={Alignment.Center}
                    theme={ButtonThemes.Auth}
                    onClick={this.completeClick.bind(this)}
                />
            </>
        );
    }
}
